// plan-03 S02: streaming text engine — bible §15

import { STREAM_CPS } from './cadence'
import { playKeypress } from '../audio/sfx'

export interface StreamOpts {
  cps?: number
  onChar?: (i: number) => void
  onDone?: () => void
  signal?: AbortSignal
  sfx?: boolean
}

// reduced-motion check
function prefersReducedMotion(): boolean {
  return document.body.classList.contains('vt-reduced-motion')
}

// skip flag — any keypress during stream completes it instantly
let skipRequested = false

function onSkipKey(): void {
  skipRequested = true
}

export function enableSkip(): void {
  skipRequested = false
  document.addEventListener('keydown', onSkipKey)
  document.addEventListener('click', onSkipKey)
}

export function disableSkip(): void {
  skipRequested = false
  document.removeEventListener('keydown', onSkipKey)
  document.removeEventListener('click', onSkipKey)
}

export async function streamText(
  target: HTMLElement,
  text: string,
  opts: StreamOpts = {},
): Promise<void> {
  const cps = opts.cps ?? STREAM_CPS
  const reduced = prefersReducedMotion()

  // reduced motion: instant
  if (reduced) {
    appendLineInstant(target, text)
    opts.onDone?.()
    return
  }

  const interval = 1000 / cps

  for (let i = 0; i < text.length; i++) {
    if (opts.signal?.aborted) return
    if (skipRequested) {
      // complete instantly
      appendLineInstant(target, text.slice(i + 1))
      skipRequested = false
      opts.onDone?.()
      return
    }

    target.textContent += text[i]
    opts.onChar?.(i)

    // SFX: rate-limited inside playKeypress() — pitch varies per char (plan-17 S04)
    if (opts.sfx !== false) {
      playKeypress(text[i])
    }

    await sleep(interval)
  }

  opts.onDone?.()
}

function appendLineInstant(target: HTMLElement, text: string): void {
  target.textContent += text
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Append a whole line (used for echo/input lines that don't stream)
export function appendLine(
  parent: HTMLElement,
  text: string,
  className?: string,
): HTMLDivElement {
  const line = document.createElement('div')
  line.className = className ?? ''
  line.textContent = text
  parent.appendChild(line)
  return line
}

// Create a streaming target (empty div) and return it
export function createStreamTarget(parent: HTMLElement, className?: string): HTMLDivElement {
  const target = document.createElement('div')
  if (className) target.className = className
  parent.appendChild(target)
  return target
}
